import { EditableComponent } from '@adobe/aem-react-editable-components';
import { FC, useState, useEffect, useCallback, useMemo } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import { Modal, Button } from '@marriott/mi-ui-library';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useSession } from '@marriott/mi-headless-utils';
import { InternationalPopupProps } from './InternationalPopup.types';
import { StyledInternationalPopup } from './InternationalPopup.styles';
import { getUserLocation } from '../../modules/utils';

export const PopupConfig = {
  emptyLabel: 'International Popup',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/popup`,
};

export const InternationalPopupComp: FC<InternationalPopupProps> = (props: InternationalPopupProps) => {
  const {
    header,
    description,
    primaryCtaLabel,
    secondaryCtaLabel,
    secondaryCtaLink,
    primaryCtaLink,
    isAuthorMode,
    countryList,
  } = props;

  const [popupOpenState, setPopupOpenState] = useState<boolean>(false);
  const { datalayer } = useSession();

  const sanitizedHtml = useMemo(() => {
    const sanitizedNode = DOMPurify.sanitize(description, { RETURN_DOM: true }) as HTMLElement;
    return sanitizedNode.innerHTML;
  }, [description]);

  const checkInternationalUser = useCallback(
    (dataLayer?: unknown): boolean => {
      const userLocation = getUserLocation(dataLayer);
      const normalizedCountryList = [...countryList];

      if (countryList?.includes('UK')) {
        normalizedCountryList.push('GB');
      }

      if (typeof userLocation === 'string' && userLocation?.length) {
        return normalizedCountryList.includes(userLocation);
      }

      return false;
    },
    [countryList]
  );

  useEffect(() => {
    if (datalayer && checkInternationalUser(datalayer)) {
      setPopupOpenState(true);
    }
  }, [datalayer, checkInternationalUser]);

  const toggleModal = () => {
    setPopupOpenState(prev => !prev);
  };

  return (
    <StyledInternationalPopup
      data-component-name="m-cobrand-InternationalPopup"
      data-testid="cobrand-InternationalPopup"
    >
      {(popupOpenState || isAuthorMode) && (
        <Modal
          className={isAuthorMode ? 'modal-wrapper' : 'd-flex justify-content-center'}
          secondaryClassName="m-auto popup-wrapper"
          show={true}
        >
          <Modal.Header
            labelText={header}
            popupHeaderOnCLoseFunc={toggleModal}
            className="py-4 pl-3 pr-4 px-md-5 pt-md-5"
          />
          <Modal.Body className="m-modal-body mt-0 mt-md-2 px-3 px-md-5 mr-4 mr-md-0 pb-0 pb-md-3 mb-2">
            <div className="m-modal-content mt-2 mt-md-0 mb-0" dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
          </Modal.Body>
          <Modal.FooterGeneric
            customClass="flex-sm-row flex-column"
            className="modal-footer m-auto pt-0 pb-md-5"
            actions={[
              <Button
                key="secondary-cta"
                isLink={true}
                href={secondaryCtaLink}
                className="custom-link t-font-m d-flex align-items-center"
                callback={toggleModal}
              >
                {secondaryCtaLabel}
              </Button>,
              <Button
                key="primary-cta"
                isLink={true}
                href={primaryCtaLink || '#'}
                className="m-button-m m-button-primary"
                callback={toggleModal}
              >
                {primaryCtaLabel}
              </Button>,
            ]}
          />
        </Modal>
      )}
    </StyledInternationalPopup>
  );
};

InternationalPopupComp.displayName = 'InternationalPopupComp';

export const InternationalPopup: FC<InternationalPopupProps> = (props: InternationalPopupProps) => {
  return (
    <EditableComponent config={PopupConfig} {...props}>
      <InternationalPopupComp {...props} />
    </EditableComponent>
  );
};
